import { useQuery } from '@tanstack/react-query';
import { Boxes, Loader2 } from 'lucide-react';
import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { partnerApi } from '../../../lib/api';
import { schemaDefinitionKeys, schemaMappingKeys } from '../../../lib/schema-definition-query-keys';
import { CUSTOM_OBJECTS_STRINGS } from './customObjectsStrings';
import { CustomObjectsTable } from './CustomObjectsTable';
import { getSchemaLabel, selectCustomObjectSchemas } from './customObjectSchemas';
import styles from './TenantCustomObjectsTab.module.css';

const SCHEMA_SEARCH_PARAM = 'schema';
const DATA_INTEGRATION_PATH = '/dashboard/settings/data-integration';

export const TenantCustomObjectsTab: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    data: schemaDefinitions,
    isLoading: isLoadingSchemas,
    error: schemaDefinitionsError,
  } = useQuery({
    queryKey: schemaDefinitionKeys.list(),
    queryFn: () => partnerApi.getSchemaDefinitions(),
  });
  const {
    data: schemaMappings,
    isLoading: isLoadingMappings,
    error: schemaMappingsError,
  } = useQuery({
    queryKey: schemaMappingKeys.all,
    queryFn: () => partnerApi.getSchemaMappings(),
  });

  const customObjectSchemas = selectCustomObjectSchemas(schemaDefinitions ?? [], schemaMappings);
  const selectedSchemaId = searchParams.get(SCHEMA_SEARCH_PARAM) ?? '';
  const selectedSchema = customObjectSchemas.find(schema => schema.id === selectedSchemaId);

  const handleSchemaChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set(SCHEMA_SEARCH_PARAM, event.target.value);
    setSearchParams(nextSearchParams, { replace: true });
  };

  const isLoading = isLoadingSchemas || isLoadingMappings;
  if (isLoading) {
    return (
      <p className={styles.status}>
        <Loader2 aria-hidden="true" />
        {CUSTOM_OBJECTS_STRINGS.loadingSchemas}
      </p>
    );
  }

  const loadError = schemaDefinitionsError ?? schemaMappingsError;
  if (loadError) {
    const loadErrorMessage = `${CUSTOM_OBJECTS_STRINGS.loadSchemasError}: ${loadError.message}`;
    return <p className={styles.error}>{loadErrorMessage}</p>;
  }

  if (customObjectSchemas.length === 0) {
    return (
      <article className={styles.emptyState}>
        <Boxes aria-hidden="true" />
        <h4>{CUSTOM_OBJECTS_STRINGS.noSchemasTitle}</h4>
        <p>{CUSTOM_OBJECTS_STRINGS.noSchemasDescription}</p>
        <Link to={DATA_INTEGRATION_PATH}>{CUSTOM_OBJECTS_STRINGS.noSchemasLink}</Link>
      </article>
    );
  }

  return (
    <section className={styles.customObjects}>
      <label className={styles.schemaPicker}>
        {CUSTOM_OBJECTS_STRINGS.schemaPickerLabel}
        <select value={selectedSchema?.id ?? ''} onChange={handleSchemaChange}>
          <option value="" disabled>
            {CUSTOM_OBJECTS_STRINGS.schemaPickerPlaceholder}
          </option>
          {customObjectSchemas.map(schema => (
            <option key={schema.id} value={schema.id}>
              {getSchemaLabel(schema)}
            </option>
          ))}
        </select>
      </label>

      {selectedSchema ? (
        <CustomObjectsTable key={selectedSchema.id} schemaDefinition={selectedSchema} />
      ) : (
        <p className={styles.prompt}>{CUSTOM_OBJECTS_STRINGS.chooseSchemaPrompt}</p>
      )}
    </section>
  );
};
